/**
 * Per-route <head> metadata.
 *
 * Single source of truth for page titles, meta descriptions and page-level
 * JSON-LD. Consumed by src/components/RouteSeo.jsx at runtime and baked into
 * static HTML at build time by prerender.js.
 *
 * Titles are kept under 60 characters and descriptions under 155 so neither
 * gets truncated in search results.
 */

import LEARN_ARTICLES from "../data/learnArticles";
import { HDMI_PRODUCTS, HDMI_FAQ } from "../pages/Display/HdmiDisplayPage"; // LANDING_PAGES_WIRED
import { PI_STEPS, PI_GUIDE_FAQ } from "../pages/Display/HdmiRaspberryPiGuidePage";
import { JW_STEPS, JW_GUIDE_FAQ } from "../pages/Display/HdmiJetsonWindowsGuidePage";
import { UART_HERO_PRODUCT, UART_FAQ } from "../pages/Display/UartDisplayPage";

const ORG_REF = { "@id": "https://www.bibaelectronics.com/#organization" };
const SITE_URL = "https://www.bibaelectronics.com";

const emsService = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": "https://www.bibaelectronics.com/ems#service",
  name: "Electronics Manufacturing Services (EMS)",
  serviceType: "Electronics Manufacturing Services",
  url: "https://www.bibaelectronics.com/ems",
  provider: ORG_REF,
  areaServed: { "@type": "Country", name: "India" },
  description:
    "SMT and through-hole PCB assembly, turnkey assembly with BOM procurement, and consigned assembly, from an ISO 9001 certified 10,000 sq ft facility in Gurgaon and Bhiwadi with 150,000 CPH placement capacity and Mirtec AOI inspection.",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "EMS capabilities",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "SMT PCB Assembly", description: "Surface-mount assembly handling 0201 and 0402 chips and fine-pitch QFN and BGA packages." } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Through-Hole Assembly", description: "Through-hole and mixed-technology assembly alongside SMT lines." } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Turnkey Assembly", description: "End-to-end build including PCB assembly, BOM procurement, display integration and testing." } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Consigned Assembly", description: "The customer supplies components; BIBA handles fabrication, assembly and testing." } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Box Build Assembly", description: "Final assembly of enclosures, cabling and sub-assemblies into a finished product." } },
    ],
  },
};

const embeddedService = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": "https://www.bibaelectronics.com/embedded#service",
  name: "Embedded Design Services",
  serviceType: "Embedded Systems Design and Development",
  url: "https://www.bibaelectronics.com/embedded",
  provider: ORG_REF,
  areaServed: { "@type": "Country", name: "India" },
  description:
    "End-to-end embedded design covering hardware and electrical design, firmware development, software design, PCB layout and mechanical design - from concept and prototyping through certification and deployment. Over 100 projects delivered since 2021.",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Embedded design disciplines",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Firmware Development", description: "Firmware for embedded devices including RTOS and IoT integration." } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Hardware and Electrical Design", description: "Schematic capture through to production-ready boards." } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "PCB Layout Design", description: "PCB layout for all electronic product types and complexities." } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Software Design", description: "Full-cycle software development for embedded and application systems." } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mechanical Design", description: "Functional mechanical systems using engineering principles and CAD." } },
    ],
  },
};

const contactPoint = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  url: "https://www.bibaelectronics.com/contact",
  mainEntity: ORG_REF,
};

export const dsyncStudioApp = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": "https://www.bibaelectronics.com/dsync-studio-features#software",
  name: "Dsync Studio",
  applicationCategory: "DesignApplication",
  operatingSystem: "Windows 10, Windows 11",
  url: "https://www.bibaelectronics.com/dsync-studio-features",
  downloadUrl: "https://apps.microsoft.com/detail/9MZ09MB4D61Z",
  softwareVersion: "1.0.0",
  description:
    "Dsync Studio is BIBA Electronics' no-code HMI design software and embedded GUI design tool for Smart UART displays. Build touchscreen human-machine interfaces on a drag-and-drop canvas, bind widgets to live UART data, and flash the finished design straight to an ESP32-based display.",
  publisher: ORG_REF,
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
  },
  screenshot: [
    "https://www.bibaelectronics.com/logo/dsync-feature-canvas.jpg",
    "https://www.bibaelectronics.com/logo/dsync-feature-dashboard.jpg",
    "https://www.bibaelectronics.com/logo/dsync-feature-templates.jpg",
    "https://www.bibaelectronics.com/logo/dsync-feature-colorpicker.jpg",
  ],
  featureList:
    "Drag-and-drop HMI canvas, Live UART data binding, Built-in data simulator, One-click ESP32 firmware flashing, Template library, PSD import, Multi-screen projects",
};

export const dsyncStudioFaq = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is Dsync Studio?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Dsync Studio is BIBA Electronics' Windows-based HMI design software for Smart UART displays. It is a no-code embedded GUI design tool: you build a touchscreen human-machine interface on a drag-and-drop canvas, bind it to live UART data, and flash it straight to the display over USB - no graphics driver stack to write by hand.",
      },
    },
    {
      "@type": "Question",
      name: "Is Dsync Studio free to download?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Dsync Studio is free to download from the Microsoft Store for Windows 10 and Windows 11.",
      },
    },
    {
      "@type": "Question",
      name: "Do I need to know how to code to use Dsync Studio?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. Dsync Studio is a no-code UI design tool: screens are built by dragging widgets (buttons, sliders, gauges, charts, keypads) onto a canvas and configuring them through property panels, not by writing display firmware.",
      },
    },
    {
      "@type": "Question",
      name: "Which displays does Dsync Studio work with?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Dsync Studio targets BIBA's Smart UART HMI display family, currently 5-inch and 7-inch panels at 800x480 resolution with GT911 capacitive touch. Picking a board in the Board Catalog auto-configures resolution, flash package and hardware profile.",
      },
    },
    {
      "@type": "Question",
      name: "Can I test my design without connecting real hardware?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. The built-in Data Simulator lets you feed test UART data into your widgets and preview live behavior before any hardware is connected.",
      },
    },
    {
      "@type": "Question",
      name: "How do I get my finished design onto the display?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Connect the display over USB, select its COM port, and flash it directly from Dsync Studio with one click. A live flash console shows progress, and the app checks and updates firmware versions automatically.",
      },
    },
  ],
};

/** Visible on /ems via <FaqSection>; the JSON-LD and the page text share this object. */
export const emsFaq = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What does BIBA Electronics manufacture?",
      acceptedAnswer: { "@type": "Answer", text: "BIBA Electronics Pvt Ltd manufactures assembled printed circuit boards (PCBAs) and supplies embedded display products. Manufacturing covers SMT and through-hole PCB assembly, turnkey builds and box build assembly. Products include TFT LCD display modules, capacitive and resistive touch panels, smart UART displays, segment and passive LCDs, SOM modules and embedded boards." },
    },
    {
      "@type": "Question",
      name: "Where is BIBA Electronics located?",
      acceptedAnswer: { "@type": "Answer", text: "BIBA Electronics is based at Plot 357E, Sector 37, Gurugram, Haryana 122004, India, with manufacturing across Gurgaon and Bhiwadi. The facility covers 10,000 sq ft over three floors. Business hours are Monday to Saturday, 9:30 AM to 6:30 PM IST." },
    },
    {
      "@type": "Question",
      name: "Is BIBA Electronics ISO certified?",
      acceptedAnswer: { "@type": "Answer", text: "Yes. BIBA Electronics operates ISO 9001 certified processes and delivers RoHS compliant assemblies, serving automotive, medical, industrial and consumer electronics customers." },
    },
    {
      "@type": "Question",
      name: "What is BIBA Electronics' PCB assembly capacity?",
      acceptedAnswer: { "@type": "Answer", text: "The production lines run at up to 150,000 CPH (components per hour). Equipment includes a DEK Horizon solder paste printer, a Yamaha YS24 high-speed mounter, Philips TopazX and YV-100X SMD mounters, a Heller 1808 reflow oven with 8+2 zones, and Mirtec AOI optical inspection for boards up to 500 x 400 mm. The lines handle 0201 and 0402 SMD chips and fine-pitch ICs including QFN and BGA." },
    },
    {
      "@type": "Question",
      name: "What is the difference between turnkey and consigned PCB assembly at BIBA?",
      acceptedAnswer: { "@type": "Answer", text: "In a turnkey engagement BIBA procures the entire bill of materials and delivers a fully assembled, tested product, including display integration. In a consigned engagement the customer supplies the components and BIBA performs fabrication, assembly and testing." },
    },
    {
      "@type": "Question",
      name: "Which display interfaces does BIBA Electronics support?",
      acceptedAnswer: { "@type": "Answer", text: "BIBA supplies TFT LCD modules with UART, MIPI, LVDS and RGB interfaces, with capacitive or resistive touch, for industrial, medical, automotive and consumer applications. Smart UART displays ship with Dsync Studio, which lets an OEM build an HMI over a serial command interface without writing a graphics driver stack." },
    },
    {
      "@type": "Question",
      name: "How do I request a quote from BIBA Electronics?",
      acceptedAnswer: { "@type": "Answer", text: "For PCB assembly, send Gerber files and a bill of materials with target volumes. For displays or touch panels, send the mechanical drawing, interface requirement and annual volume. Contact BIBA at info@bibaelectronics.com or +91 95610 91506, including on WhatsApp. The team responds within one business day." },
    },
  ],
};

/** Company-level subset shown on /about (what, where, ISO, how to quote). */
export const aboutFaq = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [0, 1, 2, 6].map((i) => emsFaq.mainEntity[i]),
};

/** Buyer-intent subset shown on the home page (what, interfaces, capacity, ISO, turnkey vs consigned, quote). */
export const homeFaq = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [0, 5, 3, 2, 4, 6].map((i) => emsFaq.mainEntity[i]),
};

/**
 * Segmented LCD knowledge-base articles (src/data/learnArticles.js) are
 * static data available at both runtime and build time -- unlike the
 * Firestore-driven product/category/blog pages, so their SEO metadata can
 * live here as the single source of truth instead of a per-component
 * <Helmet>. prerender.js reads the same data file to know which
 * /learn/<slug> routes exist and need prerendering.
 */
const learnHubJsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "@id": `${SITE_URL}/learn#collection`,
  name: "Segmented LCD Learning Center",
  url: `${SITE_URL}/learn`,
  description:
    "Segmented LCD technology, viewing modes, drive voltage, backlighting and more, explained with pros, cons and best-fit applications.",
  isPartOf: ORG_REF,
  hasPart: LEARN_ARTICLES.map((a) => ({
    "@type": "TechArticle",
    "@id": `${SITE_URL}/learn/${a.slug}#article`,
    name: a.heading,
    url: `${SITE_URL}/learn/${a.slug}`,
  })),
};

const learnArticlePageMeta = {};
for (const article of LEARN_ARTICLES) {
  const articlePath = `/learn/${article.slug}`;
  learnArticlePageMeta[articlePath] = {
    title: article.metaTitle,
    description: article.metaDescription,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      "@id": `${SITE_URL}${articlePath}#article`,
      headline: article.heading,
      description: article.metaDescription,
      url: `${SITE_URL}${articlePath}`,
      articleSection: article.category,
      author: ORG_REF,
      publisher: ORG_REF,
      mainEntityOfPage: `${SITE_URL}${articlePath}`,
      isPartOf: { "@id": `${SITE_URL}/learn#collection` },
    },
  };
}


// ── Landing pages: /hdmi-display and /uart-display ───────────────────────
// Product offers mirror the price slabs declared on the page components, so
// the JSON-LD can never drift from what the visitor sees.

const BRAND = { "@type": "Brand", name: "BIBA Electronics" };

function productSchema({ id, name, description, product, extraUrl }) {
  const prices = product.slabs.map(([, p]) => p);
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": id,
    name,
    sku: product.pn,
    mpn: product.pn,
    brand: BRAND,
    manufacturer: ORG_REF,
    image: [`${SITE_URL}${product.image}`],
    description,
    url: extraUrl,
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "INR",
      lowPrice: String(Math.min(...prices)),
      highPrice: String(Math.max(...prices)),
      offerCount: String(product.slabs.length),
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      url: `${SITE_URL}${product.route}`,
      seller: ORG_REF,
    },
  };
}

function faqSchema(faq) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

function breadcrumbSchema(name, pathname) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: "Displays", item: SITE_URL + "/tft-display" },
      { "@type": "ListItem", position: 3, name, item: SITE_URL + pathname },
    ],
  };
}

const hdmiJsonLd = [
  productSchema({
    id: SITE_URL + "/hdmi-display#b-0525",
    name: "7 inch HDMI Touch Display, 1024x600, USB capacitive touch",
    description:
      "7 inch IPS TFT with HDMI video input and USB capacitive touch. 1024x600 pixels, 500 nits, full viewing angle, 5 V supply, -20 to 70 C. Plug and play with Raspberry Pi, Jetson and PCs.",
    product: HDMI_PRODUCTS[0],
    extraUrl: SITE_URL + "/hdmi-display",
  }),
  productSchema({
    id: SITE_URL + "/hdmi-display#b-0526",
    name: "10.1 inch HDMI Touch Display, 1024x600, capacitive touch",
    description:
      "10.1 inch TFT with HDMI video input and capacitive touch over USB or I2C. 1024x600 pixels, 500 nits, 235 x 147 x 9.3 mm PCB outline, -20 to 70 C. For control panels, kiosks and industrial PCs.",
    product: HDMI_PRODUCTS[1],
    extraUrl: SITE_URL + "/hdmi-display",
  }),
  faqSchema(HDMI_FAQ),
  breadcrumbSchema("HDMI Displays", "/hdmi-display"),
];

const hdmiPiGuideJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to connect a 7 inch HDMI touch display to a Raspberry Pi",
    description:
      "Connect the BIBA 7 inch HDMI touch display to a Raspberry Pi with one HDMI cable and one USB cable. No drivers and no config.txt changes.",
    image: SITE_URL + "/landing/compat/raspberry-pi.jpg",
    totalTime: "PT5M",
    supply: [
      { "@type": "HowToSupply", name: "BIBA 7 inch HDMI touch display, part B-0525-QHB-0700-YX" },
      { "@type": "HowToSupply", name: "HDMI cable that fits the Raspberry Pi model" },
      { "@type": "HowToSupply", name: "USB cable for touch and 5 V power" },
    ],
    step: PI_STEPS.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      url: SITE_URL + "/hdmi-display/raspberry-pi-setup#step-" + (i + 1),
    })),
  },
  faqSchema(PI_GUIDE_FAQ),
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: "Displays", item: SITE_URL + "/tft-display" },
      { "@type": "ListItem", position: 3, name: "HDMI Displays", item: SITE_URL + "/hdmi-display" },
      { "@type": "ListItem", position: 4, name: "Raspberry Pi setup", item: SITE_URL + "/hdmi-display/raspberry-pi-setup" },
    ],
  },
];

const hdmiJwGuideJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to connect an HDMI touch display to an NVIDIA Jetson, a Windows PC or Ubuntu",
    description:
      "Connect the BIBA HDMI touch display to a Jetson developer kit, a Windows 10 or 11 PC or an Ubuntu machine with one HDMI cable and one USB cable. No drivers needed.",
    image: SITE_URL + "/landing/compat/jetson.jpg",
    totalTime: "PT5M",
    supply: [
      { "@type": "HowToSupply", name: "BIBA HDMI touch display, 7 inch B-0525-QHB-0700-YX or 10.1 inch B-0526-QHB-1010-YX" },
      { "@type": "HowToSupply", name: "HDMI cable" },
      { "@type": "HowToSupply", name: "USB cable for touch and 5 V power" },
    ],
    step: JW_STEPS.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      url: SITE_URL + "/hdmi-display/jetson-windows-ubuntu-setup#step-" + (i + 1),
    })),
  },
  faqSchema(JW_GUIDE_FAQ),
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: "Displays", item: SITE_URL + "/tft-display" },
      { "@type": "ListItem", position: 3, name: "HDMI Displays", item: SITE_URL + "/hdmi-display" },
      { "@type": "ListItem", position: 4, name: "Jetson, Windows and Ubuntu setup", item: SITE_URL + "/hdmi-display/jetson-windows-ubuntu-setup" },
    ],
  },
];

const uartJsonLd = [
  productSchema({
    id: SITE_URL + "/uart-display#b-1102",
    name: "7 inch Smart UART HMI Display, 800x480 IPS, capacitive touch, ESP32-S3",
    description:
      "7 inch intelligent serial display with an on-board ESP32-S3. Design screens in free Dsync Studio and drive them over UART from Arduino, ESP32, STM32 or any microcontroller. 800x480 IPS, 400 nits, capacitive touch, Wi-Fi and BLE, -20 to 70 C. Indian-made alternative to Nextion and DWIN.",
    product: UART_HERO_PRODUCT,
    extraUrl: SITE_URL + "/uart-display",
  }),
  faqSchema(UART_FAQ),
  breadcrumbSchema("Smart UART Displays", "/uart-display"),
];

const DEFAULT_META = {
  title: "BIBA Electronics | PCB Assembly & TFT Displays, Gurgaon",
  description:
    "ISO 9001 electronics manufacturer in Gurgaon. SMT PCB assembly, turnkey builds, TFT LCD modules, touch panels and embedded design for OEMs. Since 2021.",
};

/** Exact-path metadata. Keys must match the paths declared in src/App.js. */
export const PAGE_META = {
  "/": {
    title: "BIBA Electronics | PCB Assembly & TFT Displays, Gurgaon",
    description:
      "ISO 9001 electronics manufacturer in Gurgaon. SMT PCB assembly, turnkey builds, TFT LCD modules, touch panels and embedded design for OEMs. Since 2021.",
    jsonLd: homeFaq,
  },
  "/ems": {
    title: "Electronics Manufacturing Services in Gurgaon | BIBA",
    description:
      "SMT and through-hole PCB assembly, turnkey and consigned builds. ISO 9001 certified, 150,000 CPH capacity, Mirtec AOI inspection. Quote in one business day.",
    jsonLd: [emsService, emsFaq],
  },
  "/embedded": {
    title: "Embedded Design & Firmware Development | BIBA",
    description:
      "Hardware, firmware, software, PCB layout and mechanical design under one roof in Gurgaon - then manufactured in the same facility. 100+ projects delivered.",
    jsonLd: embeddedService,
  },
  "/contact": {
    title: "Contact BIBA Electronics | Gurugram, Haryana",
    description:
      "Call +91 95610 91506, WhatsApp us or email info@bibaelectronics.com. Plot 357E, Sector 37, Gurugram. Mon-Sat 9:30 AM-6:30 PM. We reply in one business day.",
    jsonLd: contactPoint,
  },
  "/about": {
    title: "About BIBA Electronics | Made in India Since 2021",
    description:
      "Founded 2021 in Gurgaon. BIBA Electronics designs and manufactures embedded electronics and displays for OEMs across industrial, medical and automotive sectors.",
    jsonLd: aboutFaq,
  },
  "/about/return-policy": {
    title: "Return Policy | BIBA Electronics",
    description:
      "BIBA Electronics replaces any defective or non-working product within 7 days of delivery, at no cost to you. See the full return policy.",
  },
  "/about/privacy-policy": {
    title: "Privacy Policy | BIBA Electronics",
    description:
      "What personal data bibaelectronics.com collects, why, who it is shared with, and the choices you have. BIBA Electronics Pvt. Ltd., Gurugram.",
  },
  "/about/terms": {
    title: "Terms of Use & Sale | BIBA Electronics",
    description:
      "Terms for using bibaelectronics.com and buying from BIBA Electronics: accounts, prices and payment, shipping, returns, cancellations and liability.",
  },
  "/about/cookie-policy": {
    title: "Cookie Policy | BIBA Electronics",
    description:
      "Which cookies bibaelectronics.com uses and why, and how to change your cookie choices at any time.",
  },
  "/about/shipping-policy": {
    title: "Shipping Policy | BIBA Electronics",
    description:
      "How BIBA Electronics ships orders across India from Gurugram: courier options and charges shown at checkout, dispatch and delivery times for in-stock and made-to-order items.",
  },
  "/tft-display": {
    title: "TFT LCD Display Modules for OEMs | BIBA Electronics",
    description:
      "Industrial TFT LCD modules with capacitive or resistive touch, supplied to Indian and export OEM volumes from Gurgaon. UART, MIPI, LVDS and RGB interfaces.",
  },
  "/hdmi-display": {
    title: "7 & 10.1 Inch HDMI Touch Display | BIBA Electronics",
    description:
      "1024x600 HDMI touch displays with USB capacitive touch, 500 nits, -20 to 70 C. Plug and play with Raspberry Pi, Jetson and PCs. Made in India, in stock, from \u20B93,700.",
    image: "https://www.bibaelectronics.com/landing/hdmi-display-7-inch-b-0525.jpg",
    jsonLd: hdmiJsonLd,
  },
  "/hdmi-display/raspberry-pi-setup": {
    title: "Raspberry Pi HDMI Touch Display Setup | BIBA",
    description:
      "Connect a 7 inch HDMI touch display to a Raspberry Pi 5, 4, 3 or Zero 2 W: the right HDMI cable, USB touch and power, no drivers, and portrait rotation.",
    image: "https://www.bibaelectronics.com/landing/compat/raspberry-pi.jpg",
    jsonLd: hdmiPiGuideJsonLd,
  },
  "/hdmi-display/jetson-windows-ubuntu-setup": {
    title: "HDMI Touch Display for Jetson, Windows, Ubuntu | BIBA",
    description:
      "Connect a 7 or 10.1 inch HDMI touch display to an NVIDIA Jetson, Windows 10/11 or Ubuntu: HDMI plus USB, driverless touch, 5 V power and screen rotation.",
    image: "https://www.bibaelectronics.com/landing/compat/jetson.jpg",
    jsonLd: hdmiJwGuideJsonLd,
  },
  "/uart-display": {
    title: "Smart UART HMI Display, 5 & 7 Inch | BIBA Electronics",
    description:
      "Serial HMI touch displays with an on-board ESP32-S3. Design screens in free Dsync Studio, drive them over UART from Arduino, ESP32 or STM32. Indian-made Nextion and DWIN alternative.",
    image: "https://www.bibaelectronics.com/landing/uart-display-7-inch-b-1102.jpg",
    jsonLd: uartJsonLd,
  },
  "/dsync-studio-features": {
    title: "Dsync Studio | HMI Design Software for Smart Displays",
    description:
      "Dsync Studio: BIBA's no-code HMI design software for Smart UART displays. Drag-and-drop canvas, live UART data binding, and one-click ESP32 flashing.",
    image: "https://www.bibaelectronics.com/logo/dsync-feature-canvas.jpg",
    jsonLd: [dsyncStudioApp, dsyncStudioFaq],
  },
  "/segmented-lcd": {
    title: "Segment & Passive LCD Displays for OEMs | BIBA",
    description:
      "Custom segment and passive LCD displays for instrumentation, appliances and industrial controls. Supplied to OEM volumes from Gurgaon, India.",
  },
  "/careers": {
    title: "Careers at BIBA Electronics | Gurugram",
    description:
      "Join an Indian electronics design and manufacturing company in Gurugram. Open roles in hardware, firmware, production and quality.",
  },
  "/learn": {
    title: "Segmented LCD Learning Center | BIBA",
    description:
      "Segmented LCD technology, viewing modes, drive voltage, backlighting and more - explained with pros, cons and best-fit applications for each option.",
    jsonLd: learnHubJsonLd,
  },
  ...learnArticlePageMeta,
};

// Own 1200x630 social-preview image per main page (public/og/, made from the
// page's own photo). Pages without one fall back to the sitewide default.
const OG_IMAGES = {
  "/": "home",
  "/ems": "ems",
  "/embedded": "embedded",
  "/contact": "contact",
  "/tft-display": "tft-display",
  "/segmented-lcd": "segmented-lcd",
};
for (const [p, name] of Object.entries(OG_IMAGES)) {
  PAGE_META[p].image = `${SITE_URL}/og/${name}.jpg`;
}

// Breadcrumb trail labels, by path. A page's trail is every ancestor path that
// has a label here, ending at the page itself (Home is always first). Pages
// whose jsonLd already carries a BreadcrumbList (the landing pages) are skipped.
const CRUMB_LABELS = {
  "/ems": "Electronics Manufacturing",
  "/embedded": "Embedded Design",
  "/contact": "Contact",
  "/tft-display": "TFT Display",
  "/segmented-lcd": "Segmented LCD",
  "/dsync-studio-features": "Dsync Studio",
  "/careers": "Careers",
  "/learn": "Learning Center",
  "/about": "About",
  "/about/return-policy": "Return Policy",
  "/about/shipping-policy": "Shipping Policy",
  "/about/privacy-policy": "Privacy Policy",
  "/about/terms": "Terms of Use & Sale",
  "/about/cookie-policy": "Cookie Policy",
};
for (const article of LEARN_ARTICLES) CRUMB_LABELS[`/learn/${article.slug}`] = article.heading;

function breadcrumbsFor(clean, meta) {
  if (clean === "/" || meta.noindex || !CRUMB_LABELS[clean]) return undefined;
  if (JSON.stringify(meta.jsonLd || "").includes('"BreadcrumbList"')) return undefined;
  const parts = clean.split("/").filter(Boolean);
  const trail = [];
  for (let i = 1; i <= parts.length; i++) {
    const p = "/" + parts.slice(0, i).join("/");
    if (CRUMB_LABELS[p]) trail.push({ name: CRUMB_LABELS[p], path: p });
  }
  return trail;
}

/**
 * Routes that exist for the application but have no search value -- cart,
 * checkout, account, order history and internal tools. These get a title so
 * the browser tab is not blank, but are marked noindex.
 */
const NOINDEX_PREFIXES = [
  ["/cart", "Your Cart"],
  ["/checkout", "Checkout"],
  ["/account", "My Account"],
  ["/login", "Sign In"],
  ["/my-orders", "My Orders"],
  ["/order/", "Order Details"],
  ["/admin-assign-courier", "Assign Courier"],
  ["/compare", "Compare Products"],
  ["/search", "Search Results"],
];

/** Resolve a pathname to its metadata. Always returns a usable object. */
export function getMetaForPath(pathname = "/") {
  const clean =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;

  const exact = PAGE_META[clean];
  if (exact) return { ...exact, path: clean, breadcrumbs: breadcrumbsFor(clean, exact) };

  for (const [prefix, label] of NOINDEX_PREFIXES) {
    if (clean === prefix || clean.startsWith(prefix)) {
      return {
        title: `${label} | BIBA Electronics`,
        description: DEFAULT_META.description,
        path: clean,
        noindex: true,
      };
    }
  }

  // Product, category and blog pages render from Firestore. Their titles are
  // set by the page component itself once the record loads; this is the
  // pre-load fallback so the tab is never blank or wrong.
  return { ...DEFAULT_META, path: clean };
}

export default PAGE_META;
